import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ExternalLink, LogIn, MousePointerClick, User, UserPlus, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/providers/api-client';
import type { UtmSourceItem, UtmSourceStatsRecord, UtmSourceStatsType } from './types';
import { UtmVisitsTab } from './UtmVisitsTab';

interface UtmSourceStatsDialogProps {
  source: UtmSourceItem | null;
  type: UtmSourceStatsType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 20;

const TYPE_LABELS: Record<UtmSourceStatsType, string> = {
  visits: 'Зашли',
  registered: 'Зарегистрировались',
  authorized: 'Авторизовались',
};

function formatNumber(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getUserDisplayName(record: UtmSourceStatsRecord): string {
  if (!record.user) {
    return record.visitorId ? `Посетитель ${record.visitorId.slice(0, 8)}` : 'Аноним';
  }

  const fullName = [record.user.firstName, record.user.lastName].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }
  if (record.user.username) {
    return `@${record.user.username}`;
  }
  if (record.user.email) {
    return record.user.email;
  }
  return `User #${record.user.id}`;
}

function getTotalByType(source: UtmSourceItem | null, type: UtmSourceStatsType | null): number {
  if (!source || !type) {
    return 0;
  }

  if (type === 'registered') {
    return source.stats.registeredCount;
  }

  if (type === 'authorized') {
    return source.stats.authorizedCount;
  }

  return source.stats.visitsCount;
}

function getIconByType(type: UtmSourceStatsType | null) {
  if (type === 'registered') {
    return UserPlus;
  }
  if (type === 'authorized') {
    return LogIn;
  }
  return MousePointerClick;
}

type ViewMode = 'records' | 'channels';

export function UtmSourceStatsDialog({ source, type: initialType, open, onOpenChange }: UtmSourceStatsDialogProps) {
  const [page, setPage] = useState(1);
  const [type, setType] = useState<UtmSourceStatsType | null>(initialType);
  const [view, setView] = useState<ViewMode>('records');
  const sourceId = source?.id ?? null;
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setPage(1);
      setType(initialType);
      setView('records');
    }
  }, [open, sourceId, initialType]);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['utm-sources', sourceId, 'stats', type, page],
    queryFn: () => apiClient.get<UtmSourceStatsRecord[]>(
      `/tags/utm-sources/${sourceId}/stats/${type}?page=${page}&limit=${PAGE_SIZE}`,
    ),
    enabled: open && sourceId !== null && type !== null,
  });

  const total = getTotalByType(source, type);
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const title = type ? TYPE_LABELS[type] : 'Статистика';
  const Icon = getIconByType(type);
  let content = (
    <div className="divide-y">
      {records.map((record) => {
        const userId = record.user?.id ?? null;
        const isClickable = userId !== null;
        const rowClass = `flex items-center gap-3 py-2.5 px-1 ${isClickable ? 'cursor-pointer hover:bg-muted/40 rounded transition-colors' : ''}`;
        return (
          <div
            key={`${type}-${record.id}`}
            className={rowClass}
            onClick={() => { if (userId !== null) { navigate(`/users/show/${userId}`); onOpenChange(false); } }}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
          >
            {record.user?.photoUrl ? (
              <img src={record.user.photoUrl} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-1">
                {getUserDisplayName(record)}
                {isClickable && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
              </p>
              {record.user?.username && (record.user.firstName || record.user.email) && (
                <p className="text-xs text-muted-foreground truncate">@{record.user.username}</p>
              )}
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatDate(record.createdAt)}
            </span>
          </div>
        );
      })}
    </div>
  );

  if (isLoading) {
    content = (
      <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
    );
  } else if (records.length === 0) {
    content = (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        Нет записей
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {title}: {source?.displayName ?? 'UTM-источник'}
            <Badge variant="secondary">{formatNumber(total)}</Badge>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Детальная статистика UTM-источника
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-5 gap-2">
          <button
            type="button"
            className={`rounded-lg border p-3 text-center transition-colors hover:bg-primary/5 hover:border-primary/20 ${view === 'records' && type === 'visits' ? 'bg-primary/5 border-primary/20' : ''}`}
            onClick={() => { setPage(1); setType('visits'); setView('records'); }}
          >
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <MousePointerClick className="h-3.5 w-3.5" />
              <span className="text-xs">Зашли</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(source?.stats.visitsCount ?? 0)}</p>
          </button>
          <button
            type="button"
            className={`rounded-lg border p-3 text-center transition-colors hover:bg-primary/5 hover:border-primary/20 ${view === 'records' && type === 'registered' ? 'bg-primary/5 border-primary/20' : ''}`}
            onClick={() => { setPage(1); setType('registered'); setView('records'); }}
          >
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <UserPlus className="h-3.5 w-3.5" />
              <span className="text-xs">Рег.</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(source?.stats.registeredCount ?? 0)}</p>
          </button>
          <button
            type="button"
            className={`rounded-lg border p-3 text-center transition-colors hover:bg-primary/5 hover:border-primary/20 ${view === 'records' && type === 'authorized' ? 'bg-primary/5 border-primary/20' : ''}`}
            onClick={() => { setPage(1); setType('authorized'); setView('records'); }}
          >
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <LogIn className="h-3.5 w-3.5" />
              <span className="text-xs">Автор.</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(source?.stats.authorizedCount ?? 0)}</p>
          </button>
          <button
            type="button"
            className={`rounded-lg border p-3 text-center transition-colors hover:bg-primary/5 hover:border-primary/20 ${view === 'channels' ? 'bg-primary/5 border-primary/40' : ''}`}
            onClick={() => { setView('channels'); }}
          >
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <MapPin className="h-3.5 w-3.5" />
              <span className="text-xs">Каналы</span>
            </div>
            <p className="text-lg font-semibold">{(source?.stats.conversionRate ?? 0).toFixed(1)}%</p>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {view === 'channels' && sourceId !== null ? (
            <UtmVisitsTab sourceId={sourceId} />
          ) : content}
        </div>

        {view === 'records' && totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
